import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formErrorMsg, setFormErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u3njf8l97n3eas3gp0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormErrorMsg('提交失败，请稍后重试');
        } else {
          setFormErrorMsg(msg);
        }
        setFormState('error');
      }
    } catch {
      setFormErrorMsg('网络错误，请稍后重试');
      setFormState('error');
    }
  };

  const reputationItems = [
    { title: '500万+', desc: '日活跃用户' },
    { title: '10万+', desc: '影视资源库' },
    { title: '99.9%', desc: '服务可用率' },
    { title: '4.8分', desc: '用户满意度' },
  ];

  return (
    <>
      <SEOMeta
        title="关于我们 - 国产在线观看"
        description="国产在线观看是专注于国产影视内容的在线观看平台，提供最新电影、电视剧、综艺、动漫免费在线观看服务。了解我们的品牌故事、口碑荣誉和联系方式。"
        keywords="国产在线观看关于我们,国产影视平台介绍,免费影视平台联系方式"
        canonicalUrl="https://dywatch.github.io/about"
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dywatch.github.io/' },
              { '@type': 'ListItem', position: 2, name: '关于我们' },
            ],
          }),
        }}
      />

      {/* AboutPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于国产在线观看',
            description: '国产在线观看是专注于国产影视内容的在线观看平台',
            url: 'https://dywatch.github.io/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '国产在线观看',
            },
          }),
        }}
      />

      <Navbar />

      <main>
        <div className="px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), href: '/' },
              { label: t('about.title') },
            ]}
          />

          <h1 className="text-xl md:text-2xl font-bold text-foreground-950 mt-4 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('about.title')}
          </h1>

          {/* Brand Intro */}
          <section className="mb-12">
            <h2 className="text-lg font-bold text-foreground-950 mb-4">{t('about.brandIntro')}</h2>
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-1">
                <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                  <p>
                    <strong>国产在线观看</strong>是一个专注于<strong>国产影视内容</strong>的在线观看平台。
                    我们致力于为观众提供最新、最全、最高清的<strong>国产影视资源</strong>，涵盖电影、电视剧、综艺、动漫、纪录片等全品类内容。
                  </p>
                  <p>
                    自成立以来，我们始终坚持"<strong>内容为王、用户至上</strong>"的理念，通过先进的技术架构和优质的服务体验，
                    为数百万用户带来便捷、流畅的<strong>在线观影体验</strong>。
                  </p>
                  <p>
                    我们的内容团队每日更新最新资源，确保用户第一时间观看到心仪的<strong>国产剧集</strong>和<strong>电影</strong>。
                    无需下载，即点即播，支持多设备流畅观看。
                  </p>
                </div>
              </div>
              <div className="shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20office%20environment%20with%20large%20screens%20displaying%20film%20content%2C%20warm%20lighting%2C%20creative%20workspace%2C%20cinematic%20decoration%2C%20professional%20corporate%20photography%2C%20clean%20modern%20design&width=600&height=400&seq=about-brand&orientation=landscape"
                  alt="国产在线观看办公环境"
                  title="国产在线观看 - 品牌办公环境"
                  className="w-full md:w-80 h-48 md:h-52 object-cover rounded-xl"
                />
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-12">
            <h2 className="text-lg font-bold text-foreground-950 mb-4">{t('about.reputation')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {reputationItems.map((item, index) => (
                <div key={index} className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                    {item.title}
                  </div>
                  <div className="text-sm text-foreground-600">{item.desc}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Partners */}
          <section className="mb-12">
            <h2 className="text-lg font-bold text-foreground-950 mb-4">{t('about.partners')}</h2>
            <div className="flex flex-wrap gap-4">
              {['爱奇艺', '腾讯视频', '优酷', '芒果TV', 'Bilibili'].map((name) => (
                <div
                  key={name}
                  className="px-6 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 whitespace-nowrap"
                >
                  {name}
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-12" id="contact">
            <h2 className="text-lg font-bold text-foreground-950 mb-2">{t('about.contactTitle')}</h2>
            <p className="text-sm text-foreground-600 mb-6">{t('about.contactDesc')}</p>

            <div className="flex flex-col md:flex-row gap-8">
              {/* Contact info */}
              <div className="flex-1 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{t('about.email')}</div>
                    <div className="text-sm text-foreground-600">contact@guochanzaixian.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{t('about.phone')}</div>
                    <div className="text-sm text-foreground-600">+86 10-8888-6666</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-accent-600 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{t('about.address')}</div>
                    <div className="text-sm text-foreground-600">{t('about.addressText')}</div>
                  </div>
                </div>
              </div>

              {/* Contact form */}
              <div className="flex-1">
                <form
                  id="about-contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4 p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1">
                      {t('about.formName')}
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1">
                      {t('about.formEmail')}
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1">
                      {t('about.formMessage')}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300/60 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                  />

                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('about.formSubmit')}
                  </button>

                  {/* Status messages */}
                  {formState === 'success' && (
                    <p className="text-sm text-green-600 text-center">{t('about.formSuccess')}</p>
                  )}
                  {formState === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formErrorMsg || t('about.formError')}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}